import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Booking from '../../../core/types/model/Booking';
import EntityName from '../../../core/enums/EntityName';
import { getFilters, getSelectedProjectId, selectFilteredBookings } from '../../../core/state/bookings/selectors';
import { generateEntityFetchListAsyncAction } from '../../../core/state/entity/generateEntityAsyncActions';
import { DATE_FORMAT_ISO } from '../../../core/constants/date';
import { formatDate } from '../../../core/utils/date';
import { BookingSortFilter } from '../types';

import NoResultsPage from '../../../components/no-results-page/NoResultsPage';
import Table from '../../../components/table/Table';
import TBody from '../../../components/table/TBody/TBody';
import ListHeader from './BookingListHeader';
import { isMobileScreenSize } from '../../../core/utils/isMobileScreenSize';
import BookingListRowMobileScreen from './BookingListRowMobileScreen';
import { RootState } from '../../../core/state/state';
import BookingDetailsModal from '../modals/booking-details/BookingDetailsModal';
import BookingListRow from './BookingListRow';
import BookingPrintZones from '../BookingPrintZones';
import { Actions } from '../../../core/state/bookings/actions';

import './BookingList.scss';

interface Props {
  isPrinting?: boolean;
}

const BookingList: React.FC<Props> = ({ isPrinting }) => {
  const dispatch = useDispatch();
  const bookings = useSelector(selectFilteredBookings);
  const selectedBooking = useSelector((state: RootState) => state.bookings.selectedBooking);
  const projectId = useSelector(getSelectedProjectId);
  const [updateBookings, setUpdateBookings] = React.useState(false);
  const { t } = useTranslation();
  const isMobileScreen = isMobileScreenSize();
  const location = useLocation<any>();

  useEffect(() => {
    return () => {
      const restore = location.state?.restore;
      if (!restore) return;

      dispatch(Actions.changeDate(restore.selectedDate));
      dispatch(Actions.resetFilters());
    };
  }, [dispatch, location.state]);

  const {
    dateRange: { start: startDate, end: endDate }
  } = useSelector(getFilters);
  const selectedDate = useSelector((state: RootState) => state.bookings.selectedDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchList = useMemo(() => generateEntityFetchListAsyncAction<Booking>(EntityName.Booking), [updateBookings]);

  const [sortFilter, setSortFilter] = React.useState<BookingSortFilter>('date');
  const [isSortAscending, setIsSortAscending] = React.useState<boolean>(true);

  useEffect(() => {
    if (projectId) {
      dispatch(
        fetchList({
          projectId,
          startDate: formatDate(isMobileScreen ? selectedDate : startDate, DATE_FORMAT_ISO),
          endDate: formatDate(isMobileScreen ? selectedDate : endDate, DATE_FORMAT_ISO)
        })
      );
    }
    setUpdateBookings(false);
  }, [dispatch, fetchList, startDate, endDate, projectId, isMobileScreen, selectedDate]);

  const sortedBookings = useMemo(() => {
    return bookings.sort((a, b) => {
      const x = isSortAscending ? a : b;
      const y = isSortAscending ? b : a;

      const xValue = x[sortFilter] ?? '';
      const yValue = y[sortFilter] ?? '';

      if (xValue === yValue) {
        return 0;
      }
      return xValue > yValue ? 1 : -1;
    });
  }, [bookings, sortFilter, isSortAscending]);

  const handleSorting = (column: BookingSortFilter) => {
    if (column === sortFilter) {
      setIsSortAscending(!isSortAscending);
    } else {
      setIsSortAscending(true);
      setSortFilter(column);
    }
  };

  return (
    <div className={`booking-list ${isMobileScreen && 'booking-list--mobile'}`}>
      {sortedBookings.length > 0 ? (
        <Table>
          <ListHeader sortFilter={sortFilter} isSortAscending={isSortAscending} handleSorting={handleSorting} />
          <TBody>
            {sortedBookings?.map((booking) =>
              isMobileScreen ? (
                <BookingListRowMobileScreen key={booking.id} booking={booking} />
              ) : (
                <BookingListRow isPrinting={isPrinting} updateBookings={() => setUpdateBookings(true)} key={booking.id} booking={booking} />
              )
            )}
          </TBody>
        </Table>
      ) : (
        <NoResultsPage
          title={t('booking.noBookings')}
          description={t('booking.noBookings.text')}
          icon="calendar"
          iconClass="booking-list__no-results-icon"
        />
      )}
      <BookingPrintZones />
      {selectedBooking && <BookingDetailsModal booking={selectedBooking} />}
    </div>
  );
};
export default BookingList;
